import React, {
	useState,
	useEffect,
	useCallback,
	createContext,
	useContext,
} from "react";
import Joyride, { CallBackProps, STATUS, EVENTS } from "react-joyride";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation, useNavigate } from "react-router-dom";
import { mainTourSteps } from "./tourSteps";
import AppRouterConfig from "../../AppRouterConfig";

interface TourContextType {
	startTour: () => void;
	resetTour: () => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const useTour = () => {
	const context = useContext(TourContext);
	if (!context) {
		throw new Error("useTour must be used within TourProvider");
	}
	return context;
};

interface TourProviderProps {
	children: React.ReactNode;
}

export const TourProvider: React.FC<TourProviderProps> = ({ children }) => {
	const { isAuthenticated, isLoading, user } = useAuth0();
	const location = useLocation();
	const navigate = useNavigate();

	const [tourState, setTourState] = useState({
		run: false,
		stepIndex: 0,
	});

	const [_showRestartButton, setShowRestartButton] = useState(false);
	const [menuOpened, setMenuOpened] = useState(false);

	// Check if we're on the dashboard page
	const isOnDashboard = location.pathname === "/dashboard";
	const isOnUploadPage =
		location.pathname === AppRouterConfig.USERCOMPANY.DATA.FILEUPLOAD;
	const isOnFuelsPage =
		location.pathname === AppRouterConfig.MANAGEMENT.FUELCOMBUSTED;
	const isOnElectricityPage =
		location.pathname === AppRouterConfig.MANAGEMENT.ELECTRICITY;

	// Check if we're on a tour-enabled page
	const isOnTourPage =
		isOnDashboard || isOnUploadPage || isOnFuelsPage || isOnElectricityPage;

	// Handle tour button visibility based on location
	useEffect(() => {
		if (isOnDashboard && isAuthenticated && !isLoading) {
			setShowRestartButton(true);
		} else {
			setShowRestartButton(false);
		}
	}, [isOnDashboard, isAuthenticated, isLoading]);

	// Handle tour auto-start and navigation
	useEffect(() => {
		// Exit early if not on a tour page, or still loading
		if (!isOnTourPage) {
			// Stop tour if it's running and we navigate to a page not part of the tour
			if (tourState.run) {
				setTourState({ run: false, stepIndex: 0 });
			}
			return;
		}

		// Don't auto-start if we're not on dashboard (only auto-start on dashboard)
		if (isLoading || !isOnDashboard) {
			return;
		}

		// Only proceed if user is authenticated and on dashboard
		if (isAuthenticated && user && isOnDashboard) {
			// Check if this is a first-time user (tour never completed)
			const tourCompleted = localStorage.getItem("arteh_tour_completed");
			const tourStartedThisSession =
				sessionStorage.getItem("arteh_tour_started");

			// Only auto-start tour for first-time users who haven't seen it this session
			if (!tourCompleted && !tourStartedThisSession) {
				// Mark that we're attempting to start the tour this session
				sessionStorage.setItem("arteh_tour_started", "true");

				// Wait for page to fully render and check if dashboard elements exist
				let retryCount = 0;
				const maxRetries = 20; // Try for up to 10 seconds

				const startTourWhenReady = () => {
					// Only proceed if we're still on the dashboard
					if (location.pathname !== "/dashboard") {
						sessionStorage.removeItem("arteh_tour_started");
						return;
					}

					// Look specifically for dashboard elements
					const dashboardCharts =
						document.querySelector(".dashboard-charts");
					const companySelector =
						document.querySelector(".company-selector");
					const menuButton = document.querySelector(".menu-button");
					const dashboardContent =
						document.querySelector(".dashboard-content");

					// Only start if we find dashboard-specific elements
					if (
						dashboardCharts ||
						companySelector ||
						menuButton ||
						dashboardContent
					) {
						setTourState({ run: true, stepIndex: 0 });
					} else if (retryCount < maxRetries) {
						retryCount++;
						// Retry after a short delay
						setTimeout(startTourWhenReady, 500);
					} else {
						// Don't start tour if dashboard elements aren't found
						// Remove the session storage flag since tour didn't actually start
						sessionStorage.removeItem("arteh_tour_started");
					}
				};

				// Initial delay to let the dashboard load
				setTimeout(startTourWhenReady, 1500);
			}
		}
	// TODO(lint): exhaustive-deps disabled — needs careful review of dep array
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAuthenticated, isLoading, user, isOnDashboard, location.pathname]);

	// Add keyboard shortcuts for testing - only work on dashboard
	useEffect(() => {
		const handleKeyPress = (e: KeyboardEvent) => {
			if (isOnDashboard && (e.ctrlKey || e.metaKey) && e.shiftKey) {
				if (e.key === "T") {
					// Start tour immediately (Ctrl/Cmd + Shift + T)
					setTourState({ run: true, stepIndex: 0 });
					setShowRestartButton(false);
				} else if (e.key === "R") {
					// Reset tour data for testing (Ctrl/Cmd + Shift + R)
					localStorage.removeItem("arteh_tour_completed");
					sessionStorage.removeItem("arteh_tour_started");
					alert(
						"Tour data reset! Refresh the page to see the tour again.",
					);
				}
			}
		};

		window.addEventListener("keydown", handleKeyPress);
		return () => window.removeEventListener("keydown", handleKeyPress);
	}, [isOnDashboard]);

	// Monitor step changes to open menu when needed and handle navigation
	useEffect(() => {
		// When we reach step 3 (dashboard link), open the menu if not already opened
		if (tourState.run && tourState.stepIndex === 3 && !menuOpened) {
			const menuButton = document.querySelector(
				".menu-button",
			) as HTMLButtonElement;
			if (menuButton) {
				menuButton.click();
				setMenuOpened(true);

				// Expand Company accordion after menu opens
				setTimeout(() => {
					const companyAccordion = Array.from(
						document.querySelectorAll(".MuiListSubheader-root"),
					).find((el) => el.textContent === "Company")
						?.parentElement as HTMLElement;
					if (companyAccordion) {
						companyAccordion.click();
					}
				}, 500);
			}
		}

		// When we reach step 5 (Welcome to Upload Page - the body message), navigate to upload page
		if (tourState.run && tourState.stepIndex === 5) {

			// Close the menu first
			if (menuOpened) {
				const backdrop = document.querySelector(
					".MuiBackdrop-root",
				) as HTMLElement;
				if (backdrop) {
					backdrop.click();
					setMenuOpened(false);
				}
			}

			// Navigate to upload page
			navigate(AppRouterConfig.USERCOMPANY.DATA.FILEUPLOAD);

			// Wait for navigation to upload page and for elements to load
			let retryCount = 0;
			const maxRetries = 20;

			const checkUploadPageReady = () => {
				const uploadPageTitle = Array.from(
					document.querySelectorAll("h2"),
				).find((el) => el.textContent === "File upload");
				const templateLink = document.querySelector(
					".upload-template-link",
				);
				const dropzoneArea = document.querySelector(
					".upload-dropzone-area",
				);

				if (uploadPageTitle && templateLink && dropzoneArea) {
					// Elements are ready, tour will continue automatically
				} else if (retryCount < maxRetries) {
					retryCount++;
					setTimeout(checkUploadPageReady, 300);
				} else {
				}
			};

			// Start checking after a brief delay to allow navigation
			setTimeout(checkUploadPageReady, 800);
		}

		// Step 9: Navigate back to dashboard and open menu for Scope 1/2 walkthrough
		if (tourState.run && tourState.stepIndex === 9) {

			// Navigate to dashboard
			navigate("/dashboard");

			// Wait a bit then open menu
			setTimeout(() => {
				if (!menuOpened) {
					const menuButton = document.querySelector(
						".menu-button",
					) as HTMLButtonElement;
					if (menuButton) {
						menuButton.click();
						setMenuOpened(true);

						// After menu opens, expand Scope 1 accordion
						setTimeout(() => {
							const scope1Accordion = Array.from(
								document.querySelectorAll(
									".MuiListSubheader-root",
								),
							).find((el) => el.textContent === "Scope 1")
								?.parentElement as HTMLElement;

							if (scope1Accordion) {
								scope1Accordion.click();

								// Scroll the fuels link into view after expansion
								setTimeout(() => {
									const fuelsLink = document.querySelector(
										".menu-fuels-combusted-link",
									);
									if (fuelsLink) {
										fuelsLink.scrollIntoView({
											behavior: "smooth",
											block: "center",
										});
									}
								}, 600);
							}
						}, 300);
					}
				}
			}, 500);
		}

		// Step 12: Navigate to Fuel Combusted page (body message triggers navigation)
		if (tourState.run && tourState.stepIndex === 12) {

			// Close menu first
			if (menuOpened) {
				const backdrop = document.querySelector(
					".MuiBackdrop-root",
				) as HTMLElement;
				if (backdrop) {
					backdrop.click();
					setMenuOpened(false);
				}
			}

			// Navigate to fuel combusted page
			setTimeout(() => {
				navigate(AppRouterConfig.MANAGEMENT.FUELCOMBUSTED);

				// Wait for fuels page to load
				let retryCount = 0;
				const maxRetries = 20;

				const checkFuelsPageReady = () => {
					const pageTitle = document.querySelector(
						".fuel-combusted-page-title",
					);
					const addButton = document.querySelector(
						".fuel-combusted-add-button",
					);
					const tableContainer = document.querySelector(
						".fuel-combusted-table-container",
					);

					if (pageTitle && addButton && tableContainer) {
						// Elements are ready, tour will continue automatically
					} else if (retryCount < maxRetries) {
						retryCount++;
						setTimeout(checkFuelsPageReady, 300);
					} else {
					}
				};

				// Start checking after a brief delay to allow navigation
				setTimeout(checkFuelsPageReady, 800);
			}, 300);
		}

		// Step 16: Navigate back to dashboard and open menu for Scope 2
		if (tourState.run && tourState.stepIndex === 16) {

			// Navigate to dashboard
			navigate("/dashboard");

			// Wait a bit then open menu
			setTimeout(() => {
				if (!menuOpened) {
					const menuButton = document.querySelector(
						".menu-button",
					) as HTMLButtonElement;
					if (menuButton) {
						menuButton.click();
						setMenuOpened(true);

						// After menu opens, expand Scope 2 accordion
						setTimeout(() => {
							const scope2Accordion = Array.from(
								document.querySelectorAll(
									".MuiListSubheader-root",
								),
							).find((el) => el.textContent === "Scope 2")
								?.parentElement as HTMLElement;

							if (scope2Accordion) {
								scope2Accordion.click();

								// Scroll the electricity link into view after expansion
								setTimeout(() => {
									const electricityLink =
										document.querySelector(
											".menu-electricity-link",
										);
									if (electricityLink) {
										electricityLink.scrollIntoView({
											behavior: "smooth",
											block: "center",
										});
									}
								}, 600);
							}
						}, 300);
					}
				}
			}, 500);
		}

		// Step 19: Navigate to Electricity page (body message triggers navigation)
		if (tourState.run && tourState.stepIndex === 19) {

			// Close menu first
			if (menuOpened) {
				const backdrop = document.querySelector(
					".MuiBackdrop-root",
				) as HTMLElement;
				if (backdrop) {
					backdrop.click();
					setMenuOpened(false);
				}
			}

			// Navigate to electricity page
			setTimeout(() => {
				navigate(AppRouterConfig.MANAGEMENT.ELECTRICITY);

				// Wait for electricity page to load
				let retryCount = 0;
				const maxRetries = 20;

				const checkElectricityPageReady = () => {
					const pageTitle = document.querySelector(
						".electricity-page-title",
					);
					const addButton = document.querySelector(
						".electricity-add-button",
					);
					const tableContainer = document.querySelector(
						".electricity-table-container",
					);

					if (pageTitle && addButton && tableContainer) {
						// Elements are ready, tour will continue automatically
					} else if (retryCount < maxRetries) {
						retryCount++;
						setTimeout(checkElectricityPageReady, 300);
					} else {
					}
				};

				// Start checking after a brief delay to allow navigation
				setTimeout(checkElectricityPageReady, 800);
			}, 300);
		}

		// Close menu when tour ends
		if (!tourState.run && menuOpened) {
			const backdrop = document.querySelector(
				".MuiBackdrop-root",
			) as HTMLElement;
			if (backdrop) {
				backdrop.click();
				setMenuOpened(false);
			}
		}
	}, [tourState.stepIndex, tourState.run, menuOpened, navigate]);

	const handleJoyrideCallback = useCallback((data: CallBackProps) => {
		const { status, type, index } = data;

		// Handle tour completion or skip
		if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
			if (status === STATUS.FINISHED) {
				// Mark tour as completed in localStorage so it won't auto-start again
				localStorage.setItem("arteh_tour_completed", "true");
			} else if (status === STATUS.SKIPPED) {
				// Mark tour as completed even if skipped, so it won't auto-start again
				localStorage.setItem("arteh_tour_completed", "true");
			}

			setTourState((prev) => ({ ...prev, run: false }));
			setShowRestartButton(true);
		}

		// Handle errors or missing targets
		if (type === EVENTS.TARGET_NOT_FOUND) {
		}

		// Update step index
		if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
			setTourState((prev) => ({ ...prev, stepIndex: index + 1 }));
		}
	}, []);

	const startTour = useCallback(() => {
		setTourState({
			run: true,
			stepIndex: 0,
		});
		setShowRestartButton(false);
		setMenuOpened(false);
	}, []);

	const resetTour = useCallback(() => {
		// When manually restarting, clear the completed flag to allow tour to run
		// But keep session storage to track current session
		startTour();
	}, [startTour]);

	const joyrideStyles = {
		options: {
			primaryColor: "#5DBEC3",
			backgroundColor: "#ffffff",
			textColor: "#2c3e50",
			overlayColor: "rgba(0, 0, 0, 0.5)",
			arrowColor: "#ffffff",
			width: 420,
			zIndex: 10000,
		},
		tooltip: {
			borderRadius: 8,
			fontSize: 15,
		},
		tooltipContainer: {
			textAlign: "left" as const,
		},
		tooltipContent: {
			padding: "20px 16px",
		},
		buttonNext: {
			backgroundColor: "#5DBEC3",
			borderRadius: 4,
			fontSize: 14,
			padding: "8px 16px",
		},
		buttonBack: {
			color: "#5DBEC3",
			fontSize: 14,
		},
		buttonSkip: {
			color: "#999",
			fontSize: 14,
		},
		spotlight: {
			borderRadius: 8,
		},
	};

	return (
		<TourContext.Provider value={{ startTour, resetTour }}>
			{children}

			<Joyride
				callback={handleJoyrideCallback}
				continuous
				run={tourState.run}
				scrollToFirstStep={tourState.stepIndex === 0}
				disableScrolling={
					(tourState.stepIndex >= 5 && tourState.stepIndex <= 8) ||
					(tourState.stepIndex >= 12 && tourState.stepIndex <= 15) ||
					(tourState.stepIndex >= 19 && tourState.stepIndex <= 22)
				}
				showProgress
				showSkipButton
				steps={mainTourSteps}
				stepIndex={tourState.stepIndex}
				styles={joyrideStyles}
				locale={{
					back: "Back",
					close: "Close",
					last: "Finish",
					next: "Next",
					skip: "Skip Tour",
				}}
				floaterProps={{
					disableAnimation: false,
					hideArrow: false,
				}}
				disableOverlayClose={false}
				hideCloseButton={false}
				spotlightClicks={true}
			/>
		</TourContext.Provider>
	);
};

export default TourProvider;
